
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useToast } from "@/components/ui/use-toast";
import { ContactFormValues } from "@/types/dashboard";
import DynamicContactForm from "@/components/forms/DynamicContactForm";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { useMutation } from "@tanstack/react-query";

const AddContact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingContact, setExistingContact] = useState<ContactFormValues | null>(null);
  const [loading, setLoading] = useState(false);

  // Check if we're editing an existing contact
  const editContactId = searchParams.get('edit');
  const isEditing = !!editContactId;

  // Load existing contact data if editing
  useEffect(() => {
    const loadExistingContact = async () => {
      if (!isEditing || !editContactId || !user?.id) return;

      setLoading(true);
      try {
        const { data: contactData, error } = await supabase
          .from('contacts')
          .select(`
            *,
            tags:contact_tags(
              id,
              tag:tags(id, label, type)
            ),
            custom_fields(*)
          `)
          .eq('id', editContactId)
          .eq('user_id', user.id)
          .single();

        if (error) throw error;

        // Transform to ContactFormValues format
        const formData: ContactFormValues = {
          name: contactData.name,
          email: contactData.email || '',
          phone: contactData.phone || '',
          birthdate: contactData.birthdate ? new Date(contactData.birthdate) : undefined,
          notes: contactData.notes || '',
          photoUrl: contactData.photo_url || '',
          relationshipType: contactData.relationship_type || 'Other',
          customRelationshipLabel: contactData.custom_relationship_label || '',
          instagramHandle: contactData.instagram_handle || '',
          facebookHandle: contactData.facebook_handle || '',
          isFavorite: contactData.is_favorite || false,
          lastInteractionDate: contactData.last_interaction_date ? new Date(contactData.last_interaction_date) : undefined,
          // Read relationship-specific fields from database columns
          loveLanguage: (contactData as any).love_language || '',
          firstDate: (contactData as any).first_date ? new Date((contactData as any).first_date) : undefined,
          anniversaryDate: (contactData as any).anniversary_date ? new Date((contactData as any).anniversary_date) : undefined,
          anniversaryType: (contactData as any).anniversary_type || '',
          friendshipStartDate: (contactData as any).friendship_start_date ? new Date((contactData as any).friendship_start_date) : undefined,
          context: (contactData as any).context || '',
          insideJokesOrMemories: (contactData as any).inside_jokes_or_memories || '',
          lastHangoutDate: (contactData as any).last_hangout_date ? new Date((contactData as any).last_hangout_date) : undefined,
          companyOrProject: (contactData as any).company_or_project || '',
          preferredCommunicationStyle: (contactData as any).preferred_communication_style || '',
          parentType: (contactData as any).parent_type || '',
          healthNotes: (contactData as any).health_notes || '',
          weddingDate: (contactData as any).wedding_date ? new Date((contactData as any).wedding_date) : undefined,
          marriageAnniversary: (contactData as any).marriage_anniversary ? new Date((contactData as any).marriage_anniversary) : undefined,
          spousePreferences: (contactData as any).spouse_preferences || '',
          // Process user-defined custom fields
          customFields: contactData.custom_fields?.map((field: any) => ({
            id: field.id,
            label: field.label,
            value: field.value,
            isDate: field.is_date
          })) || [],
          tags: contactData.tags?.map((tagRelation: any) => ({
            id: tagRelation.tag.id,
            label: tagRelation.tag.label,
            type: tagRelation.tag.type
          })) || []
        };

        setExistingContact(formData);
      } catch (error) {
        toast({
          title: "Error loading contact",
          description: "Could not load contact data for editing.",
          variant: "destructive",
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadExistingContact();
  }, [isEditing, editContactId, user?.id, navigate, toast]);

  // Create a mutation for adding/updating contacts
  const addContactMutation = useMutation({
    mutationFn: async (values: ContactFormValues) => {
      // First check if user is authenticated
      if (!user || !user.id) {
        toast({
          title: "Authentication error",
          description: "You must be logged in to add contacts",
          variant: "destructive",
        });
        throw new Error("You must be logged in to add contacts");
      }
      setIsSubmitting(true);
      let photoUrl = null;
      
      try {
        // Handle avatar upload if provided
        if (values.avatarFile) {
          const fileExt = values.avatarFile.name.split('.').pop();
          const fileName = `${uuidv4()}.${fileExt}`;
          const filePath = `${user.id}/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('contact-photos')
            .upload(filePath, values.avatarFile, {
              cacheControl: '3600',
              upsert: false
            });
            
          if (uploadError) {
            // Provide more specific error messages
            if (uploadError.message?.includes('duplicate') || uploadError.message?.includes('already exists')) {
              // If file already exists, try with upsert
              const { error: retryError } = await supabase.storage
                .from('contact-photos')
                .upload(filePath, values.avatarFile, {
                  cacheControl: '3600',
                  upsert: true
                });
              if (retryError) {
                throw new Error('Failed to upload profile picture. Please try again.');
              }
            } else {
              throw new Error('Failed to upload profile picture. Please try again.');
            }
          }
          
          // Get public URL for the uploaded image
          const { data: { publicUrl } } = supabase.storage
            .from('contact-photos')
            .getPublicUrl(filePath);
            
          photoUrl = publicUrl;
        }
        
        // Prepare base contact data - include all relevant database fields
        const baseContactData: any = {
          name: values.name,
          email: values.email || null,
          phone: values.phone || null,
          notes: values.notes || null,
          birthdate: values.birthdate ? new Date(values.birthdate).toISOString() : null,
          relationship_type: values.relationshipType || null,
          custom_relationship_label: values.customRelationshipLabel || null,
          is_favorite: values.isFavorite || false,
          instagram_handle: values.instagramHandle || null,
          facebook_handle: values.facebookHandle || null,
          last_interaction_date: values.lastInteractionDate ? new Date(values.lastInteractionDate).toISOString() : null,
          // Relationship-specific fields
          love_language: values.loveLanguage || null,
          first_date: values.firstDate ? new Date(values.firstDate).toISOString() : null,
          anniversary_date: values.anniversaryDate ? new Date(values.anniversaryDate).toISOString() : null,
          anniversary_type: values.anniversaryType || null,
          friendship_start_date: values.friendshipStartDate ? new Date(values.friendshipStartDate).toISOString() : null,
          context: values.context || null,
          inside_jokes_or_memories: values.insideJokesOrMemories || null,
          last_hangout_date: values.lastHangoutDate ? new Date(values.lastHangoutDate).toISOString() : null,
          company_or_project: values.companyOrProject || null,
          preferred_communication_style: values.preferredCommunicationStyle || null,
          parent_type: values.parentType || null,
          health_notes: values.healthNotes || null,
          wedding_date: values.weddingDate ? new Date(values.weddingDate).toISOString() : null,
          marriage_anniversary: values.marriageAnniversary ? new Date(values.marriageAnniversary).toISOString() : null,
          spouse_preferences: values.spousePreferences || null
        };

        // Handle photo URL - only update if new photo uploaded or explicitly provided
        if (photoUrl) {
          baseContactData.photo_url = photoUrl;
        } else if (!isEditing) {
          baseContactData.photo_url = null;
        } else if (values.photoUrl) {
          // Preserve existing photo URL when editing
          baseContactData.photo_url = values.photoUrl;
        }

        
        // Insert or update the contact in Supabase
        let data, error;
        if (isEditing && editContactId) {
          // Update existing contact
          const updateResult = await supabase
            .from('contacts')
            .update(baseContactData)
            .eq('id', editContactId)
            .eq('user_id', user.id)
            .select()
            .single();
          data = updateResult.data;
          error = updateResult.error;
        } else {
          // Insert new contact - include user_id
          const insertData = {
            ...baseContactData,
            user_id: user.id
          };
          const insertResult = await supabase
            .from('contacts')
            .insert([insertData])
            .select()
            .single();
          data = insertResult.data;
          error = insertResult.error;
        }
          
        if (error) {
          throw error;
        }
        
        // Verify the data was actually saved by querying it back
        if (isEditing && editContactId) {
          const { data: verifyData } = await supabase
            .from('contacts')
            .select('*')
            .eq('id', editContactId)
            .single();
        }
        
        // Handle tags - for updates, clear existing tags first
        if (isEditing && editContactId) {
          // Delete existing contact_tags relationships
          await supabase
            .from('contact_tags')
            .delete()
            .eq('contact_id', editContactId);
        }
        
        // If there are tags, add them to the tags table and link to the contact
        if (values.tags && values.tags.length > 0) {
          // First, insert any new tags
          for (const tag of values.tags) {
            // Check if the tag already exists
            const { data: existingTag } = await supabase
              .from('tags')
              .select('id')
              .eq('label', tag.label)
              .eq('user_id', user.id)
              .maybeSingle();
              
            let tagId;
            
            if (!existingTag) {
              // Create the tag if it doesn't exist
              const { data: newTag, error: tagError } = await supabase
                .from('tags')
                .insert({
                  label: tag.label,
                  type: tag.type || 'interest',
                  user_id: user.id
                })
                .select()
                .single();
                
              if (tagError) {
              } else {
                tagId = newTag?.id;
              }
            } else {
              tagId = existingTag.id;
            }
            
            // Link the tag to the contact
            if (tagId) {
              await supabase
                .from('contact_tags')
                .insert({
                  contact_id: data.id,
                  tag_id: tagId
                });
            }
          }
        }
        
        // Handle custom fields - for updates, clear existing custom fields first
        if (isEditing && editContactId) {
          // Delete existing custom fields
          await supabase
            .from('custom_fields')
            .delete()
            .eq('contact_id', editContactId);
        }
        
        // Handle only user-defined custom fields (not relationship-specific fields)
        if (values.customFields && values.customFields.length > 0) {
          const customFieldsData = values.customFields.map(field => ({
            label: field.label,
            value: field.value,
            is_date: field.isDate || false,
            contact_id: data.id
          }));
          
          await supabase
            .from('custom_fields')
            .insert(customFieldsData);
        }
        
        return data;
      } catch (error) {
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    onSuccess: (data) => {
      toast({
        title: isEditing ? "Contact updated successfully" : "Contact added successfully",
        description: `${data.name} has been ${isEditing ? 'updated' : 'added to your contacts'}.`,
      });
      if (isEditing) {
        navigate(`/contacts/${editContactId}`);
      } else {
        navigate("/dashboard");
      }
    },
    onError: (error) => {
      toast({
        title: isEditing ? "Failed to update contact" : "Failed to add contact",
        description: (error as Error).message || "An unknown error occurred",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = (values: ContactFormValues) => {
    addContactMutation.mutate(values);
  };

  return (
    <DashboardLayout>
      <div className="pb-10">
        <div className="container mx-auto px-4 pt-4">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4"
            onClick={() => navigate("/dashboard")}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <h1 className="mb-6 text-2xl font-bold">{isEditing ? 'Edit Contact' : 'Add New Contact'}</h1>
          
          <div className="mx-auto max-w-2xl rounded-lg bg-white p-6 shadow-sm">
            {!user ? (
              <div className="text-center py-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-4">You must be logged in to add contacts.</p>
                <Button 
                  onClick={() => navigate("/auth")} 
                  className="bg-[#FF8370] hover:bg-[#e76a58]"
                >
                  Log in / Sign up
                </Button>
              </div>
            ) : (
              loading ? (
                <div className="flex justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading contact data...</p>
                  </div>
                </div>
              ) : (
                <DynamicContactForm 
                  onSubmit={handleSubmit} 
                  isSubmitting={isSubmitting || addContactMutation.isPending}
                  initialValues={existingContact || undefined}
                />
              )
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddContact;
